import { useEffect, useState, type FormEvent } from 'react'
import { ThemeToggle } from '../components/ThemeToggle'
import { adminApi, ApiError, type AdminPrincipal, type AdminOrganization } from '../lib/api'
import { Connections, CONNECTIONS } from './Connections'
import { Details } from './Details'
import { Users } from './Users'
import { NewCustomer } from './NewCustomer'
import { PlatformSettings } from './PlatformSettings'
import { AdminFeedback } from './Feedback'

/** The super admin console. A different door: its own origin, its own cookie,
 *  its own table, no organization scoping anywhere. An org admin never sees
 *  that it exists. */
export function AdminApp() {
  const [me, setMe] = useState<AdminPrincipal | null>(null)
  const [checked, setChecked] = useState(false)

  useEffect(() => {
    adminApi
      .whoami()
      .then(setMe)
      .catch(() => setMe(null))
      .finally(() => setChecked(true))
  }, [])

  if (!checked) return null
  return me ? (
    <Console me={me} onSignedOut={() => setMe(null)} />
  ) : (
    <AdminSignIn onSignedIn={setMe} />
  )
}

function AdminSignIn({ onSignedIn }: { onSignedIn: (a: AdminPrincipal) => void }) {
  const [login, setLogin] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function submit(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      onSignedIn(await adminApi.signIn(login, password))
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Sign-in failed.')
      setBusy(false)
    }
  }

  return (
    <div className="signin-wrap">
      {/* The console chrome has one, but that only exists AFTER signing in --
          so a super admin who chose light inside the console landed back here
          in light with no way to change it. Same trap as the dispatcher's
          sign-in screen. */}
      <div className="signin-theme">
        <ThemeToggle />
      </div>
      <form className="signin admin" onSubmit={submit}>
        <h1>Super admin</h1>
        <div className="org">Setup console</div>
        {error && <div className="notice">{error}</div>}
        <div className="field">
          <label htmlFor="alogin">Login</label>
          <input id="alogin" value={login} autoFocus
                 onChange={(e) => setLogin(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="apw">Password</label>
          <input id="apw" type="password" value={password}
                 onChange={(e) => setPassword(e.target.value)} />
        </div>
        <button className="primary" disabled={busy || !login || !password}>
          {busy ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
    </div>
  )
}

function Console({ me, onSignedOut }: { me: AdminPrincipal; onSignedOut: () => void }) {
  const [orgs, setOrgs] = useState<AdminOrganization[]>([])
  const [selected, setSelected] = useState<AdminOrganization | null>(null)
  const [tab, setTab] = useState<string>('details')
  const [creating, setCreating] = useState(false)
  // Settings above every customer -- the platform's Google keys. Swaps the
  // panel the way `creating` does, because it belongs to no customer and
  // drawing it under one customer's tabs would say it did.
  const [platform, setPlatform] = useState(false)
  // The feedback board (065): every customer's bug reports and feature
  // requests. Above every customer like Platform settings, so it swaps the
  // panel the same way. The count is what holds something unread.
  const [feedbackOpen, setFeedbackOpen] = useState(false)
  const [feedbackUnread, setFeedbackUnread] = useState(0)
  useEffect(() => {
    let alive = true
    const load = () =>
      adminApi.feedbackUnread()
        .then((r) => { if (alive) setFeedbackUnread(r.unread) })
        .catch(() => {})
    load()
    const t = setInterval(load, 60_000)
    return () => { alive = false; clearInterval(t) }
  }, [])
  const [error, setError] = useState<string | null>(null)

  async function loadOrgs(selectSlug?: string) {
    try {
      const list = await adminApi.organizations()
      setOrgs(list)
      setSelected((current) => {
        if (selectSlug) return list.find((o) => o.slug === selectSlug) ?? list[0] ?? null
        return list.find((o) => o.id === current?.id) ?? list[0] ?? null
      })
      setError(null)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not load organizations.')
    }
  }

  useEffect(() => {
    loadOrgs()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const TABS = [
    { key: 'details', label: 'Details' },
    { key: 'users', label: 'Logins' },
    ...CONNECTIONS.map((c) => ({ key: c.kind, label: c.tab })),
  ]

  return (
    <div className="shell">
      <div className="chrome admin">
        <div className="org-name">Super admin</div>

        {/* The picker lives in the chrome, so which customer you are
            configuring is always on screen rather than something you have to
            remember from a list you scrolled past. */}
        <select
          className="orgpick"
          value={selected?.id ?? ''}
          onChange={(e) => {
            setSelected(orgs.find((o) => o.id === e.target.value) ?? null)
            setTab('details')
            setCreating(false)
            setPlatform(false)
            setFeedbackOpen(false)
          }}
        >
          {orgs.length === 0 && <option value="">No customers yet</option>}
          {orgs.map((o) => (
            <option key={o.id} value={o.id}>
              {o.name}
              {o.active ? '' : '  (inactive)'}
            </option>
          ))}
        </select>

        <button className="ghost" onClick={() => { setCreating(true); setPlatform(false); setFeedbackOpen(false) }}>
          + New customer
        </button>

        <button className="ghost" onClick={() => { setPlatform(true); setCreating(false); setFeedbackOpen(false) }}>
          Platform settings
        </button>

        <button className="ghost" onClick={() => { setFeedbackOpen(true); setPlatform(false); setCreating(false) }}>
          Feedback{feedbackUnread > 0 && <span className="fb-badge">{feedbackUnread}</span>}
        </button>

        <div className="spacer" />

        <ThemeToggle />
        <div className="who">
          <div>
            <div style={{ fontSize: 13 }}>{me.display_name}</div>
            <div className="r">Above all organizations</div>
          </div>
        </div>
        <button className="ghost" onClick={() => adminApi.signOut().finally(onSignedOut)}>
          Sign out
        </button>
      </div>

      {!me.secrets_available && (
        <div className="banner">
          <strong>SECRET_KEY is not set.</strong> Credentials cannot be
          encrypted, so no connection can be saved.
        </div>
      )}

      <div className="console">
        <div className={`panel${feedbackOpen ? ' fb-panel-wide' : ''}`}>
          {error && <div className="notice">{error}</div>}

          {feedbackOpen ? (
            <AdminFeedback orgs={orgs} onCount={setFeedbackUnread}
                           onClose={() => setFeedbackOpen(false)} />
          ) : platform ? (
            <div className="panelbody">
              <PlatformSettings onClose={() => setPlatform(false)} />
            </div>
          ) : creating ? (
            <NewCustomer
              onCancel={() => setCreating(false)}
              onCreated={async (slug) => {
                setCreating(false)
                await loadOrgs(slug)
                setTab('details')
              }}
            />
          ) : !selected ? (
            <div className="work-empty">
              <div className="mark">No customers yet</div>
              <p>Use “New customer” above to create the first one.</p>
            </div>
          ) : (
            <>
              <div className="tabs sticky">
                {TABS.map((t) => (
                  <button
                    key={t.key}
                    className={`tab ${t.key === tab ? 'on' : ''}`}
                    onClick={() => setTab(t.key)}
                  >
                    {t.label}
                  </button>
                ))}
              </div>

              {/* Only this scrolls. The chrome and the tab strip stay put, so
                  the page itself never moves under you. */}
              <div className="panelbody">
                {tab === 'details' && (
                  <Details org={selected} onSaved={() => loadOrgs(selected.slug)} />
                )}
                {tab === 'users' && <Users org={selected} />}
                {tab !== 'details' && tab !== 'users' && (
                  <Connections org={selected} only={tab} />
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
